import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SearchBar from "./search-bar";
import { Form } from "react-bootstrap";

import styles from "./search-bar.module.scss";
import ResultsContainer from "./results-container";

/**
 * StateSearch component to search for bankers by state.
 *
 * @param {Object} props - The component props.
 * @param {string} props.query - The current state query.
 * @param {Array} props.states - The list of states to search from.
 * @param {Function} props.onStateSearch - Callback function to handle state search.
 *
 * @returns {JSX.Element} The rendered StateSearch component.
 */
const StateSearch = ({ query = "", states = [], onStateSearch = (newState) => {} }) => {
  return (
    <Form.Group id="state-select" className="dropdown">
      <Form.Control
        id="select-state-to-search"
        className="border-radius-6 select-input-text custom-select-chevron"
        title="Select a State"
        aria-labelledby="select-state-label"
        tabIndex={0}
        as="select"
        defaultValue={""}
        value={query}
        onChange={(e) => {
          onStateSearch(e.target.value);
        }}
      >
        {states.map(({ node }, index) => (
          <option
            key={index}
            value={node.StateName !== "New Mexico" ? node.StateName : "NewMexico"}
            id={node.branchStateId}
            className="dropdown-content"
          >
            {node.StateName}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  );
};

/**
 * BankersByNameSearchBar component to search for bankers by name.
 *
 * @param {Object} props - The component props.
 * @param {string} props.query - The current query string.
 * @param {Function} props.clearQuery - Function to clear the query.
 * @param {boolean} props.focused - Whether the search bar is focused.
 * @param {Function} props.onFocus - Callback function to handle focus event.
 * @param {Object} props.querySuggestions - Object containing query suggestions.
 * @param {Function} props.onInputChange - Callback function to handle input change.
 * @param {Function} props.onInputKeyDown - Callback function to handle key down event.
 * @param {Function} props.onBankerSearch - Callback function to handle banker search.
 * @param {Function} props.onPopulateQueryButtonClick - Callback function to handle populate query button click.
 * @returns
 */
const BankersByNameSearchBar = ({
  query = "",
  clearQuery = () => {},
  focused = false,
  onFocus = (event) => {},
  querySuggestions = { hits: [] },
  onInputChange = (event) => {},
  onInputKeyDown = (event) => {},
  onBankerSearch = (newQuery) => {},
  onPopulateQueryButtonClick = (newQuery) => {}
}) => {
  // Handlers for searches that utilize the onSearch function.
  const onResultSearchButtonClick = (newQuery, _suggestionId) => {
    onBankerSearch(newQuery);
  };
  const onSearchButtonClick = (event) => {
    event.preventDefault();
    onBankerSearch(query);
  };

  return (
    <>
      <form className="row" autoComplete="off">
        <div className={`col-12 row my-2 ${query === "" ? "mb-3" : ""} mx-0 px-0`}>
          <div className={`d-flex flex-grow-1 px-3 ${styles.searchPageInputContainer}`}>
            <SearchBar
              query={query}
              clearQuery={clearQuery}
              onInputChange={onInputChange}
              onSearchButtonClick={onSearchButtonClick}
              onInputKeyDown={onInputKeyDown}
              onFocus={onFocus}
              isBankerSearch={true}
              id="ssi-banker-name"
              label="Search WaFd Bank"
            />
          </div>
        </div>
      </form>
      <ResultsContainer
        focused={focused}
        query={query}
        querySuggestions={querySuggestions}
        onResultSearchButtonClick={onResultSearchButtonClick}
        onPopulateQueryButtonClick={onPopulateQueryButtonClick}
        isBankers={true}
      />
    </>
  );
};

/**
 * BankersSearchBar component to search for bankers by state or name.
 *
 * @param {Object} props - The component props.
 * @param {string} props.query - The current query string.
 * @param {Function} props.clearQuery - Function to clear the query.
 * @param {Array} props.querySuggestions - Array of query suggestions.
 * @param {0 | 1} props.searchType - Type of search (0 for state, 1 for banker name).
 * @param {Function} props.onFocus - Callback function to handle focus event.
 * @param {boolean} props.shouldFocus - Whether the search bar should be focused.
 * @param {Function} props.shouldFocusCallback - Callback function to handle focus callback.
 * @param {boolean} props.shouldBlur - Whether the search bar should be blurred.
 * @param {Function} props.shouldBlurCallback - Callback function to handle blur callback.
 * @param {Array} props.states - Array of states for state search.
 * @param {Function} props.onStateSearch - Callback function to handle state search.
 * @param {Function} props.onInputChange - Callback function to handle input change.
 * @param {Function} props.onInputKeyDown - Callback function to handle key down event.
 * @param {Function} props.onBankerSearch - Callback function to handle banker search.
 * @param {Function} props.onPopulateQueryButtonClick - Callback function to handle populate query button click.
 *
 * @returns {JSX.Element} The rendered BankersSearchBar component.
 */
const BankersSearchBar = ({
  query = "",
  clearQuery = () => {},
  querySuggestions,
  searchType = 0,
  onFocus = (event) => {},
  shouldFocus = false,
  shouldFocusCallback = () => {},
  shouldBlur = false,
  shouldBlurCallback = () => {},
  states = [],
  onStateSearch = (newState) => {},
  onInputChange = (event) => {},
  onInputKeyDown = (event) => {},
  onBankerSearch = (newQuery) => {},
  onPopulateQueryButtonClick = (newQuery) => {}
}) => {
  const [focused, setFocused] = useState(false);
  const searchFormContainerRef = useRef(null);

  useEffect(() => {
    if (shouldFocus) {
      setFocused(true);
      searchFormContainerRef.current.focus();
      shouldFocusCallback();
    }
  }, [shouldFocus, shouldFocusCallback]);

  useEffect(() => {
    if (shouldBlur) {
      console.log("Trying to blur component");
      setFocused(false);
      searchFormContainerRef.current.blur();
      shouldBlurCallback();
    }
  }, [shouldBlur, shouldBlurCallback]);

  // Add a handler to track when the user clicks outside the search form
  useEffect(() => {
    const onClickHandler = (event) => {
      if (!searchFormContainerRef.current.contains(event.target)) {
        setFocused(false);
      }
    };

    window.addEventListener("click", onClickHandler);
    return () => window.removeEventListener("click", onClickHandler);
  }, []);

  const isStateSearch = searchType === 0;
  const isBankerNameSearch = searchType === 1;

  return (
    <>
      <div className={`${focused ? "d-block" : "d-none"} ${styles.placeholderSearchInputContainer}`} />
      <div
        ref={searchFormContainerRef}
        className={`col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6 ${
          focused ? styles.searchFormContainerBankersFocused : styles.searchFormContainerBlurred
        } border-radius-6`}
      >
        {/* Title */}
        <div className="row mx-0 justify-content-between">
          <p id="select-state-label" className="mb-0 mt-2 text-gray-60" style={{ paddingLeft: 3 }}>
            {isStateSearch && "State"}
            {isBankerNameSearch && "Banker Name"}
          </p>
        </div>

        {/* State Search */}
        {isStateSearch && <StateSearch query={query} states={states} onStateSearch={onStateSearch} />}

        {/* Banker Search */}
        {isBankerNameSearch && (
          <BankersByNameSearchBar
            query={query}
            clearQuery={clearQuery}
            focused={focused}
            onFocus={onFocus}
            querySuggestions={querySuggestions}
            onInputChange={onInputChange}
            onInputKeyDown={onInputKeyDown}
            onBankerSearch={onBankerSearch}
            onPopulateQueryButtonClick={onPopulateQueryButtonClick}
          />
        )}
      </div>
    </>
  );
};

export default BankersSearchBar;

BankersSearchBar.propTypes = {
  query: PropTypes.string.isRequired,
  clearQuery: PropTypes.func.isRequired,
  querySuggestions: PropTypes.array.isRequired,
  searchType: PropTypes.oneOf([0, 1]).isRequired,
  onFocus: PropTypes.func.isRequired,
  shouldFocus: PropTypes.bool.isRequired,
  shouldFocusCallback: PropTypes.func.isRequired,
  shouldBlur: PropTypes.bool.isRequired,
  shouldBlurCallback: PropTypes.func.isRequired,
  states: PropTypes.array.isRequired,
  onStateSearch: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onInputKeyDown: PropTypes.func.isRequired,
  onBankerSearch: PropTypes.func.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired
};

StateSearch.propTypes = {
  query: PropTypes.string.isRequired,
  states: PropTypes.array.isRequired,
  onStateSearch: PropTypes.func.isRequired
};

BankersByNameSearchBar.propTypes = {
  query: PropTypes.string.isRequired,
  clearQuery: PropTypes.func.isRequired,
  focused: PropTypes.bool.isRequired,
  onFocus: PropTypes.func.isRequired,
  querySuggestions: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onInputKeyDown: PropTypes.func.isRequired,
  onBankerSearch: PropTypes.func.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired
};
